// To add a new post, append a new object to POSTS; /blog and /blog/[slug] update automatically.

export interface PostSection {
  id: string;
  headingPtBr: string;
  bodyPtBr: string;
  bulletsPtBr?: string[];
  faqPtBr?: Array<{ question: string; answer: string }>;
}

export interface Post {
  slug: string;
  title: string;
  excerpt: string;
  dateISO: string;
  readingTimeMinutes: number;
  sections: PostSection[];
}

export const POSTS: Post[] = [
  {
    slug: 'isencao-ir-doenca-grave-aposentados',
    title: 'Isenção de imposto de renda para aposentados com doenças graves',
    excerpt: 'Entenda quem pode ter direito, quais documentos costumam ser solicitados e como funciona o pedido.',
    dateISO: '2024-01-15',
    readingTimeMinutes: 8,
    sections: [
      {
        id: 'quem-tem-direito',
        headingPtBr: 'Quem tem direito',
        bodyPtBr: 'A isenção de imposto de renda sobre proventos de aposentadoria, reforma ou pensão é um direito previsto em lei para pessoas diagnosticadas com doenças graves específicas. A legislação brasileira lista condições como cardiopatia grave, neoplasia maligna, AIDS, Parkinson, esclerose múltipla, entre outras. É importante destacar que o direito existe independentemente do estágio da doença, bastando a comprovação do diagnóstico por meio de laudo médico oficial. Aposentados, pensionistas e pessoas em reforma militar podem solicitar o benefício, desde que recebam rendimentos de aposentadoria ou pensão e possuam o diagnóstico de uma das doenças previstas na Lei 7.713/88.',
        bulletsPtBr: [
          'Aposentados do INSS ou de regimes próprios',
          'Pensionistas que recebem pensão por morte',
          'Militares reformados',
          'Portadores de doenças graves listadas em lei'
        ]
      },
      {
        id: 'documentos-comuns',
        headingPtBr: 'Documentos comuns',
        bodyPtBr: 'Para solicitar a isenção, é necessário reunir documentação que comprove tanto a condição de aposentado ou pensionista quanto o diagnóstico da doença grave. O laudo médico é o documento central do pedido e deve ser emitido por serviço médico oficial (da União, Estados, Distrito Federal ou Municípios). Esse laudo precisa conter informações detalhadas sobre o diagnóstico, incluindo o CID da doença, a data do diagnóstico e a assinatura do médico responsável. Além disso, é comum que sejam solicitados exames complementares que confirmem o diagnóstico.',
        bulletsPtBr: [
          'Laudo médico oficial com CID da doença',
          'Exames que comprovem o diagnóstico',
          'Documento de identidade e CPF',
          'Comprovante de recebimento de aposentadoria ou pensão',
          'Formulário de requerimento preenchido'
        ]
      },
      {
        id: 'passo-a-passo',
        headingPtBr: 'Passo a passo (visão geral)',
        bodyPtBr: 'O processo de solicitação da isenção geralmente começa com a obtenção do laudo médico oficial. Com o laudo em mãos, o próximo passo é reunir os demais documentos necessários e protocolar o pedido junto ao órgão pagador da aposentadoria ou pensão. Para aposentados do INSS, o pedido pode ser feito diretamente nas agências ou pelo site Meu INSS. Servidores públicos devem procurar o departamento de recursos humanos do seu órgão. Após a análise, que pode levar algumas semanas, o órgão emitirá uma decisão. Se deferido, a isenção passa a valer a partir da data do diagnóstico ou do requerimento, conforme o caso, podendo gerar restituição de valores pagos indevidamente.',
        bulletsPtBr: [
          'Obter laudo médico oficial com o diagnóstico',
          'Reunir documentação complementar',
          'Protocolar pedido no órgão pagador',
          'Aguardar análise administrativa',
          'Receber decisão e eventual restituição'
        ]
      },
      {
        id: 'erros-comuns',
        headingPtBr: 'Erros comuns',
        bodyPtBr: 'Muitos pedidos de isenção são negados por questões documentais que poderiam ser evitadas. Um dos erros mais frequentes é apresentar laudo médico particular em vez de laudo oficial, o que não é aceito pela legislação. Outro problema comum é a falta de informações essenciais no laudo, como o CID completo da doença ou a data do diagnóstico. Também é importante atentar para o prazo: embora não haja prazo para solicitar a isenção, quanto antes o pedido for feito, maior será o período de restituição de valores. Além disso, alguns requerentes esquecem de solicitar a restituição dos valores pagos indevidamente nos últimos cinco anos, perdendo esse direito.',
        bulletsPtBr: [
          'Apresentar laudo médico particular em vez de oficial',
          'Laudo sem CID ou data do diagnóstico',
          'Não solicitar restituição de valores pagos',
          'Deixar de atualizar o laudo quando necessário',
          'Não acompanhar o andamento do processo'
        ]
      },
      {
        id: 'perguntas-frequentes',
        headingPtBr: 'Perguntas frequentes',
        bodyPtBr: 'Reunimos as dúvidas mais comuns sobre a isenção de imposto de renda para aposentados com doenças graves.',
        faqPtBr: [
          {
            question: 'A isenção vale para qualquer tipo de rendimento?',
            answer: 'Não. A isenção se aplica apenas aos proventos de aposentadoria, reforma ou pensão. Outros rendimentos, como aluguéis ou aplicações financeiras, continuam sendo tributados normalmente.'
          },
          {
            question: 'Preciso renovar o laudo médico periodicamente?',
            answer: 'Depende da doença e da orientação do órgão pagador. Para doenças crônicas e permanentes, geralmente não há necessidade de renovação. Porém, alguns órgãos podem solicitar atualização do laudo após determinado período.'
          },
          {
            question: 'Posso pedir restituição de valores pagos antes do diagnóstico?',
            answer: 'Sim, é possível solicitar a restituição dos valores de imposto de renda pagos nos últimos cinco anos anteriores ao pedido, desde que você já tivesse o diagnóstico nesse período.'
          },
          {
            question: 'E se meu pedido for negado?',
            answer: 'É possível recorrer da decisão administrativa ou, se necessário, buscar o reconhecimento do direito na esfera judicial. Cada caso deve ser analisado individualmente para definir a melhor estratégia.'
          }
        ]
      }
    ]
  },
  {
    slug: 'aposentadoria-pcd',
    title: 'Aposentadoria PcD: regras e como comprovar',
    excerpt: 'Veja requisitos, avaliação e exemplos de documentos que ajudam a comprovar a condição.',
    dateISO: '2024-01-22',
    readingTimeMinutes: 10,
    sections: [
      {
        id: 'quem-tem-direito',
        headingPtBr: 'Quem tem direito',
        bodyPtBr: 'A aposentadoria da pessoa com deficiência é um benefício previdenciário destinado a segurados do INSS que comprovem a condição de pessoa com deficiência durante o período de contribuição. A Lei Complementar 142/2013 estabelece que a deficiência pode ser de natureza física, mental, intelectual ou sensorial, e deve ser avaliada de forma biopsicossocial. O direito à aposentadoria PcD existe em duas modalidades: por idade (com requisitos reduzidos) e por tempo de contribuição (com tempo variável conforme o grau de deficiência). É fundamental entender que a deficiência deve ter existido durante o período contributivo, não sendo suficiente adquiri-la apenas no momento do pedido de aposentadoria.',
        bulletsPtBr: [
          'Segurados do INSS com deficiência comprovada',
          'Deficiência física, mental, intelectual ou sensorial',
          'Impedimentos de longo prazo (mínimo 2 anos)',
          'Avaliação biopsicossocial realizada por perícia médica e social'
        ]
      },
      {
        id: 'documentos-comuns',
        headingPtBr: 'Documentos comuns',
        bodyPtBr: 'A comprovação da condição de pessoa com deficiência é feita por meio de avaliação pericial realizada pelo INSS, que considera aspectos médicos e sociais. Para essa avaliação, é importante reunir toda a documentação médica disponível que demonstre a existência e a duração da deficiência. Laudos médicos detalhados, exames de imagem, relatórios de tratamentos, receitas de medicamentos de uso contínuo e documentos que mostrem adaptações necessárias no dia a dia são fundamentais. Quanto mais completo for o conjunto de documentos apresentados, melhor será a avaliação da sua condição.',
        bulletsPtBr: [
          'Laudos médicos com descrição detalhada da deficiência',
          'Exames complementares (radiografias, ressonâncias, etc.)',
          'Relatórios de tratamentos e acompanhamentos',
          'Receitas de medicamentos de uso contínuo',
          'Documentos de identidade e CPF',
          'CNIS (Cadastro Nacional de Informações Sociais)',
          'Carteira de trabalho e comprovantes de contribuição'
        ]
      },
      {
        id: 'passo-a-passo',
        headingPtBr: 'Passo a passo (visão geral)',
        bodyPtBr: 'O processo de solicitação da aposentadoria PcD começa com o agendamento da perícia médica e social no INSS. Esse agendamento pode ser feito pelo telefone 135 ou pelo site/aplicativo Meu INSS. Antes da perícia, é essencial reunir toda a documentação médica que comprove a deficiência ao longo do tempo. No dia da perícia, você passará por avaliação médica e também por avaliação social, que considera como a deficiência impacta sua vida cotidiana e profissional. Após a perícia, o INSS analisará o pedido considerando tanto a comprovação da deficiência quanto o cumprimento dos requisitos de idade ou tempo de contribuição. O resultado pode levar algumas semanas, e é importante acompanhar o processo pelo Meu INSS.',
        bulletsPtBr: [
          'Agendar perícia médica e social no INSS',
          'Reunir documentação médica completa',
          'Comparecer à perícia com todos os documentos',
          'Aguardar análise do pedido',
          'Acompanhar resultado pelo Meu INSS',
          'Receber decisão e eventual carta de concessão'
        ]
      },
      {
        id: 'erros-comuns',
        headingPtBr: 'Erros comuns',
        bodyPtBr: 'Um dos erros mais frequentes é não reunir documentação médica suficiente para comprovar a deficiência durante todo o período contributivo. Muitas pessoas apresentam apenas laudos recentes, quando na verdade precisam demonstrar que a condição existe há anos. Outro problema comum é não entender a diferença entre doença e deficiência: nem toda doença configura deficiência para fins previdenciários. A deficiência deve causar impedimentos de longo prazo que, em interação com barreiras, obstruam a participação plena na sociedade. Também é comum que segurados não se preparem adequadamente para a avaliação social, que é tão importante quanto a médica.',
        bulletsPtBr: [
          'Apresentar apenas documentos médicos recentes',
          'Confundir doença com deficiência',
          'Não se preparar para a avaliação social',
          'Deixar de apresentar documentos de adaptações necessárias',
          'Não acompanhar o processo após a perícia',
          'Desistir após uma negativa sem buscar orientação'
        ]
      },
      {
        id: 'perguntas-frequentes',
        headingPtBr: 'Perguntas frequentes',
        bodyPtBr: 'Esclarecemos as principais dúvidas sobre a aposentadoria da pessoa com deficiência.',
        faqPtBr: [
          {
            question: 'Qual a diferença entre aposentadoria PcD por idade e por tempo de contribuição?',
            answer: 'A aposentadoria por idade exige 60 anos (homem) ou 55 anos (mulher) mais 15 anos de contribuição na condição de PcD. Já a por tempo de contribuição varia conforme o grau: deficiência grave (25 anos homem/20 mulher), moderada (29/24) ou leve (33/28).'
          },
          {
            question: 'Preciso ter a deficiência desde o nascimento?',
            answer: 'Não. A deficiência pode ter sido adquirida em qualquer momento da vida. O importante é comprovar que ela existiu durante o período de contribuição que você está considerando para a aposentadoria.'
          },
          {
            question: 'O que é a avaliação biopsicossocial?',
            answer: 'É uma avaliação que considera não apenas aspectos médicos da deficiência, mas também como ela impacta sua vida social, profissional e cotidiana. Por isso, além da perícia médica, há também uma avaliação social realizada por assistente social.'
          },
          {
            question: 'Posso converter tempo comum em tempo de PcD?',
            answer: 'Não é possível converter tempo trabalhado sem deficiência em tempo como PcD. Porém, você pode somar períodos: o tempo como PcD conta para a aposentadoria PcD, e todo o tempo (com e sem deficiência) pode ser usado para outras modalidades de aposentadoria.'
          }
        ]
      }
    ]
  },
  {
    slug: 'revisao-aposentadoria-contribuicao-pcd',
    title: 'Revisão da aposentadoria por contribuição para PcD',
    excerpt: 'Quando a revisão pode fazer sentido e quais pontos costumam gerar dúvidas.',
    dateISO: '2024-02-05',
    readingTimeMinutes: 9,
    sections: [
      {
        id: 'quem-tem-direito',
        headingPtBr: 'Quem tem direito',
        bodyPtBr: 'A revisão da aposentadoria para reconhecimento de tempo como pessoa com deficiência é um direito de quem já está aposentado por tempo de contribuição comum, mas que trabalhou parte do período na condição de PcD sem que isso tenha sido reconhecido na concessão do benefício. Se você se aposentou pela regra comum mas tinha deficiência durante parte ou todo o período contributivo, pode ter direito a uma revisão que reconheça esse tempo especial. Isso pode resultar em aumento do valor da aposentadoria ou em aposentadoria mais vantajosa. O direito à revisão existe desde que respeitado o prazo decadencial de 10 anos contados da concessão do benefício.',
        bulletsPtBr: [
          'Aposentados por tempo de contribuição comum',
          'Pessoas que trabalharam com deficiência não reconhecida',
          'Benefício concedido há menos de 10 anos',
          'Possibilidade de comprovar a deficiência no período contributivo'
        ]
      },
      {
        id: 'documentos-comuns',
        headingPtBr: 'Documentos comuns',
        bodyPtBr: 'Para solicitar a revisão, é necessário reunir documentação que comprove a existência da deficiência durante o período em que você trabalhou e contribuiu para a previdência. Essa é a parte mais desafiadora, pois muitas vezes estamos falando de períodos antigos. Laudos médicos da época, prontuários hospitalares, exames antigos, receitas médicas, atestados de afastamento, documentos de tratamentos realizados e até mesmo fotos e relatos que demonstrem a condição são importantes. Quanto mais documentos você conseguir reunir, especialmente aqueles contemporâneos ao período que deseja revisar, maiores as chances de sucesso.',
        bulletsPtBr: [
          'Carta de concessão da aposentadoria atual',
          'Laudos e exames médicos do período contributivo',
          'Prontuários médicos antigos',
          'Receitas e documentos de tratamentos',
          'Atestados e relatórios médicos da época',
          'Documentos que demonstrem adaptações necessárias',
          'CNIS atualizado'
        ]
      },
      {
        id: 'passo-a-passo',
        headingPtBr: 'Passo a passo (visão geral)',
        bodyPtBr: 'O processo de revisão geralmente começa com uma análise detalhada do seu histórico contributivo e da documentação médica disponível. É importante identificar exatamente quais períodos podem ser reconhecidos como tempo de PcD e qual seria o impacto no valor do benefício. Com essa análise feita, o próximo passo é reunir a documentação comprobatória e protocolar o pedido de revisão, que pode ser feito administrativamente no INSS ou judicialmente. A via administrativa é mais rápida, mas nem sempre resulta em deferimento. Na via judicial, há necessidade de realização de perícia médica retrospectiva, que avaliará se você tinha deficiência no período alegado. O processo pode levar meses ou até anos, dependendo da via escolhida.',
        bulletsPtBr: [
          'Analisar histórico contributivo e documentação',
          'Identificar períodos passíveis de reconhecimento',
          'Calcular impacto no valor do benefício',
          'Reunir documentação comprobatória',
          'Protocolar pedido (administrativo ou judicial)',
          'Realizar perícia médica retrospectiva',
          'Aguardar decisão e eventual pagamento de diferenças'
        ]
      },
      {
        id: 'erros-comuns',
        headingPtBr: 'Erros comuns',
        bodyPtBr: 'Um erro frequente é solicitar a revisão sem antes fazer uma análise detalhada para verificar se ela realmente será vantajosa. Nem sempre o reconhecimento de tempo como PcD resulta em aumento do benefício, dependendo das regras aplicáveis e do histórico contributivo. Outro problema comum é não reunir documentação suficiente do período antigo, dificultando a comprovação da deficiência na época. Muitas pessoas também perdem o prazo decadencial de 10 anos, o que impede a revisão. Além disso, é importante entender que a perícia retrospectiva é diferente da perícia comum: o perito avaliará se você tinha deficiência no passado, com base nos documentos apresentados.',
        bulletsPtBr: [
          'Não calcular se a revisão será vantajosa',
          'Deixar passar o prazo de 10 anos',
          'Não reunir documentação do período antigo',
          'Confundir perícia retrospectiva com perícia comum',
          'Desistir após negativa administrativa sem avaliar via judicial',
          'Não considerar custos e tempo do processo'
        ]
      },
      {
        id: 'perguntas-frequentes',
        headingPtBr: 'Perguntas frequentes',
        bodyPtBr: 'Respondemos as dúvidas mais comuns sobre revisão de aposentadoria para reconhecimento de tempo como PcD.',
        faqPtBr: [
          {
            question: 'Toda revisão de aposentadoria para PcD aumenta o valor do benefício?',
            answer: 'Não necessariamente. O aumento depende de diversos fatores, como as regras de cálculo aplicáveis, seu histórico contributivo e o período reconhecido como PcD. Por isso, é fundamental fazer uma análise prévia antes de solicitar a revisão.'
          },
          {
            question: 'Posso pedir revisão se já estou aposentado há mais de 10 anos?',
            answer: 'Infelizmente não. Existe um prazo decadencial de 10 anos contados da data da concessão do benefício. Após esse prazo, não é mais possível solicitar a revisão administrativa. Há discussões judiciais sobre esse prazo, mas a regra geral é essa.'
          },
          {
            question: 'Como funciona a perícia retrospectiva?',
            answer: 'Na perícia retrospectiva, o médico perito analisa a documentação médica antiga para avaliar se você tinha deficiência no período alegado. Por isso, é fundamental apresentar documentos da época, como laudos, exames e prontuários.'
          },
          {
            question: 'Vale mais a pena fazer a revisão no INSS ou na Justiça?',
            answer: 'A via administrativa é mais rápida e sem custos, mas tem menor taxa de sucesso. A via judicial demora mais e pode ter custos (honorários advocatícios), mas geralmente tem melhores resultados. A escolha depende da análise do seu caso específico.'
          }
        ]
      }
    ]
  }
];
